/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { cx, css } from "@emotion/css";
import { Callout, Drawer, ExternalLink, Note } from "@octopusdeploy/design-system-components";
import { DebouncedTextWithLabel as DebounceText, DebounceValue } from "@octopusdeploy/design-system-octopus-components";
import { fontSize, fontWeight, space } from "@octopusdeploy/design-system-tokens";
import type { ActionTemplateParameterResource, FeedResource, ProcessTemplateParameterResource } from "@octopusdeploy/octopus-server-client";
import { FeedType, feedTypeSupportsExtraction, PackageAcquisitionLocation, PackageSelectionMode } from "@octopusdeploy/octopus-server-client";
import { cloneDeep } from "lodash";
import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from "react";
import type { DeepPartial } from "redux";
import type { RunOn } from "~/areas/projects/components/Process/types";
import { GeneratePackageNameFromId } from "~/components/Actions/commonActionHelpers";
import { HelmImageReplacePathPopover } from "~/components/Actions/script/HelmImageReplacePathPopover";
import type { ScriptPackageReference } from "~/components/Actions/script/scriptPackageReference";
import PackageDownloadOptions from "~/components/PackageDownloadOptions/PackageDownloadOptions";
import DeferredPackageSelector from "~/components/PackageSelector/DeferredPackageSelector";
import PackageSelectorPopover from "~/components/PackageSelector/PackageSelectorPopover";
import { withBoundField } from "~/components/form/BoundField/BoundField";
import { BoundStringCheckbox } from "~/components/form/BoundField/StringCheckbox";
import isBound from "~/components/form/BoundField/isBound";
import { required, Text } from "~/components/form/index";
const styles = {
    container: css({
        padding: space[16],
        paddingBottom: space[32],
    }),
    errorContainer: css({
        marginBottom: space[16],
    }),
    subtitle: css({
        fontWeight: fontWeight["600"],
        fontSize: fontSize["base"],
        marginBottom: space[16],
        "& span": {
            marginRight: space[8],
        },
        "&.noMargin": {
            marginBottom: 0,
        },
    }),
};
type InnerActions = {
    getValidatedPackageReference: () => ScriptPackageReference | false;
};
type ScriptPackageReferenceDrawerInnerProps = {
    packageReference: ScriptPackageReference;
    runOn?: RunOn;
    feeds: FeedResource[];
    localNames: string[];
    projectId: string;
    parameters?: ActionTemplateParameterResource[];
    isAlwaysDeferredPackage?: boolean;
    hideAcquisitionChoices?: boolean;
    refreshFeeds(): Promise<void>;
    onParameterAdded?: (parameters: ProcessTemplateParameterResource[]) => void;
    allowHelmReplacementPathSetting?: boolean;
};
const BoundDebounceText = withBoundField(DebounceValue(Text));
const ScriptPackageReferenceDrawerInner = forwardRef<InnerActions, ScriptPackageReferenceDrawerInnerProps>((props, ref) => {
    const { runOn, feeds, localNames, projectId, parameters, isAlwaysDeferredPackage, hideAcquisitionChoices, refreshFeeds, onParameterAdded, allowHelmReplacementPathSetting } = props;
    //store the original values
    const originalNameRef = useRef(props.packageReference.Name);
    const originalPackageIdRef = useRef(props.packageReference.PackageId);
    const originalExtractRef = useRef(props.packageReference.Properties.Extract);
    const originalHelmReplacementPath = useRef(props.packageReference.Properties.HelmReplacementPath);
    const [packageReference, setPackageReference] = useState<ScriptPackageReference>(() => {
        const cloned = cloneDeep(props.packageReference);
        if (!cloned.Properties.SelectionMode) {
            cloned.Properties.SelectionMode = isAlwaysDeferredPackage === true ? PackageSelectionMode.Deferred : PackageSelectionMode.Immediate;
        }
        return cloned;
    });
    const [showNameChangeWarning, setShowNameChangeWarning] = useState<boolean>(false);
    const [validationError, setValidationError] = useState<string | undefined>();
    const updatePackageReference = (updated: DeepPartial<ScriptPackageReference>) => {
        setPackageReference((prev) => ({
            ...prev,
            ...updated,
            Properties: {
                ...prev.Properties,
                ...updated.Properties,
            },
        }));
        //clear any validation error when something changes
        setValidationError(undefined);
    };
    const findFeed = useCallback((feedId: string) => feeds.find((f) => f.Id === feedId), [feeds]);
    const feed = useMemo(() => findFeed(packageReference.FeedId), [findFeed, packageReference.FeedId]);
    // Show the option to extract if the feed-type supports it
    // or if the feed is bound to a variable.
    const shouldShowExtractOption = (feed: FeedResource | undefined) => (!feed || feedTypeSupportsExtraction(feed.FeedType)) && packageReference.AcquisitionLocation !== PackageAcquisitionLocation.NotAcquired;
    const shouldShowNameChangeWarning = (name: string) => {
        // If the name is changing from a previous value, then we'll show a warning regarding external dependencies
        return !!name && !!originalNameRef.current && name !== originalNameRef.current;
    };
    const onPackageIdChange = (packageId: string) => {
        let updated: DeepPartial<ScriptPackageReference> = {
            PackageId: packageId,
        };
        // If `Name` was the default value (the previous package ID), then update it to the new package ID
        if (!!packageId && !isBound(packageId) && !(!!originalNameRef.current && originalNameRef.current !== GeneratePackageNameFromId(originalPackageIdRef.current))) {
            const name = GeneratePackageNameFromId(packageId);
            updated = {
                ...updated,
                Name: name,
            };
            setShowNameChangeWarning(shouldShowNameChangeWarning(name));
        }
        updatePackageReference(updated);
    };
    const onFeedIdChange = (feedId: string) => {
        const newFeed = findFeed(feedId);
        let updated: DeepPartial<ScriptPackageReference> = {
            FeedId: feedId,
        };
        if (!shouldShowExtractOption(newFeed)) {
            updated = {
                ...updated,
                Properties: {
                    Extract: "False",
                },
            };
        }
        updatePackageReference(updated);
    };
    const onPackageParameterChange = (packageParameter: string) => {
        let updated: DeepPartial<ScriptPackageReference> = {
            PackageId: "",
            Properties: {
                PackageParameterName: packageParameter,
            },
        };
        if (!!packageParameter && !packageReference.Name) {
            const name = GeneratePackageNameFromId(packageParameter);
            updated = {
                ...updated,
                Name: name,
            };
            setShowNameChangeWarning(shouldShowNameChangeWarning(name));
        }
        updatePackageReference(updated);
    };
    useImperativeHandle(ref, () => ({
        getValidatedPackageReference: () => {
            if (packageReference.Properties.SelectionMode === PackageSelectionMode.Immediate) {
                if (!packageReference.Name) {
                    setValidationError("Please enter a name");
                    return false;
                }
                else if (!packageReference.PackageId) {
                    setValidationError("Please select a package ID");
                    return false;
                }
                else if (!packageReference.FeedId) {
                    setValidationError("Please select a feed");
                    return false;
                }
            }
            else {
                if (!packageReference.Properties.PackageParameterName) {
                    setValidationError("Please select a package parameter");
                    return false;
                }
            }
            return packageReference;
        },
    }));
    const showExtract = shouldShowExtractOption(feed);
    return (<div>
            {validationError && (<div className={styles.errorContainer}>
                    <Callout type={"danger"} title={validationError}/>
                </div>)}
            <div className={styles.subtitle}>
                <span>Package Feed</span>
                <PackageSelectorPopover />
            </div>
            {feed?.FeedType === FeedType.AwsElasticContainerRegistry && "UseMachineCredentials" in feed && feed.UseMachineCredentials && (<>
                    <Callout type={"warning"} title={"This AWS Elastic Container Registry feed uses machine credentials"}>
                        If credentials are not set on this server, package search will not find any results. Please enter the full package ID.
                    </Callout>
                    <br />
                </>)}

            <DeferredPackageSelector packageId={packageReference.PackageId} feedId={packageReference.FeedId} packageVersion={packageReference.Version} onPackageIdChange={(packageId) => onPackageIdChange(packageId)} onFeedIdChange={(feedId) => onFeedIdChange(feedId)} onPackageVersionChange={(version) => updatePackageReference({ Version: version })} localNames={localNames} projectId={projectId} feeds={feeds} refreshFeeds={async () => await refreshFeeds()} 
    // Deferred package selection properties
    parameters={parameters} packageSelectionMode={packageReference.Properties.SelectionMode!} onPackageSelectionModeChange={(packageSelectionMode) => updatePackageReference({
            Properties: {
                SelectionMode: packageSelectionMode,
                PackageParameterName: "",
            },
        })} packageParameterName={packageReference.Properties.PackageParameterName || ""} onPackageParameterChange={(packageParameter) => onPackageParameterChange(packageParameter)} isAlwaysDeferredPackage={isAlwaysDeferredPackage} onParameterAdded={onParameterAdded}/>
            {showNameChangeWarning && (<Callout type={"warning"} title={"The name has been changed"}>
                    If you have external dependencies on this field (custom scripts, build-server plugins, external feed triggers, etc) these will need to be updated.
                </Callout>)}
            <DebounceText label="Name" value={packageReference.Name || ""} onChange={(name) => updatePackageReference({ Name: name })} validate={required("Please supply a Name")}/>
            <Note>
                Provide a unique name to identify the package reference. Learn about <ExternalLink href="ScriptStepPackageReferenceName">package names</ExternalLink>.
            </Note>
            {allowHelmReplacementPathSetting && (<>
                    <BoundDebounceText label="Helm image tag path (optional)" boundRows={1} resetValue={originalHelmReplacementPath.current} variableLookup={{ localNames: localNames }} value={packageReference.Properties.HelmReplacementPath || ""} onChange={(helmValueReference) => updatePackageReference({ Properties: { HelmReplacementPath: helmValueReference } })}/>
                    <Note>
                        Using Helm? Provide the path to the field containing the image tag in your values file e.g. <code>image.tag</code> <HelmImageReplacePathPopover />
                    </Note>
                </>)}
            <br />
            {!hideAcquisitionChoices && (<>
                    {feed?.PackageAcquisitionLocationOptions?.length && <div className={cx(styles.subtitle, "noMargin")}>Package Acquisition</div>}
                    <PackageDownloadOptions packageAcquisitionLocation={packageReference.AcquisitionLocation} onPackageAcquisitionLocationChanged={(acquisitionLocation) => updatePackageReference({
                AcquisitionLocation: acquisitionLocation,
            })} runOn={runOn} showNotAcquiredOption={true} feed={feed} projectId={projectId} localNames={localNames}/>
                    {showExtract && (<>
                            <BoundStringCheckbox variableLookup={{
                    localNames: localNames,
                }} resetValue={originalExtractRef.current} value={packageReference.Properties.Extract} label="Extract package during deployment" onChange={(extract) => updatePackageReference({
                    Properties: {
                        Extract: extract,
                    },
                })}/>
                            <Note>
                                Learn about <ExternalLink href="ScriptStepPackageReferencesFromCustomScripts">accessing package references from a custom script</ExternalLink>.
                            </Note>
                        </>)}
                </>)}
        </div>);
});
type ScriptPackageReferenceDrawerProps = ScriptPackageReferenceDrawerInnerProps & {
    open: boolean;
    onClose: () => void;
    onChange(PackageReference: ScriptPackageReference): boolean;
};
export const ScriptPackageReferenceDrawer = (props: ScriptPackageReferenceDrawerProps) => {
    const { open, onClose, onChange, packageReference, ...rest } = props;
    const innerRef = useRef<InnerActions>(null);
    const onSave = () => {
        if (!innerRef.current) {
            return;
        }
        const result = innerRef.current.getValidatedPackageReference();
        if (result === false) {
            return;
        }
        onChange(result);
        onClose();
    };
    //if we already have a name, we must be updating
    const primaryActionLabel = packageReference?.Name ? "Update Package" : "Add Package";
    return (<Drawer isOpen={open} onClose={onClose} title={"Reference a package"} variant={"default"} primaryButtonLabel={primaryActionLabel} onPrimaryButtonClick={onSave} width={"narrow"}>
            <div className={styles.container}>{packageReference && <ScriptPackageReferenceDrawerInner ref={innerRef} packageReference={packageReference} {...rest}/>}</div>
        </Drawer>);
};
